import React from "react";
import {string, ref} from "yup";
import {useParams} from "react-router";
import {
    Form,
    FormGroup,
    View,
    Stack,
    SubmitButton,
    TextInput,
    ErrorMessage,
} from "@unibuddy/patron";
import useRelativeRouter from "ubcommunity-shared/src/Navigation/useRelativeRouter/useRelativeRouter";
import {useChangeCommunityPasswordMutation} from "ubcommunity-shared/src/types";
import useAuth from "ubcommunity-shared/src/Auth/hooks/useAuth";
import {PASSWORD_MIN_LENGTH} from "ubcommunity-shared/src/Auth/Signup/SignupStep1";
import {usePageTitle} from "ubcommunity-shared/src/Hooks/usePageTitle";
import {PageTitles} from "ubcommunity-shared/src/constants";
import {MutationErrorHandler} from "ubcommunity-shared/src/General/Errors/MutationErrorHandler";

export function ChangePasswordForm() {
    const {push} = useRelativeRouter();
    const {userId, token} = useParams<{userId: string; token: string; userType: string}>();
    const {user} = useAuth();
    const [change, {error}] = useChangeCommunityPasswordMutation();

    usePageTitle(PageTitles.CHANGE_PASSWORD);

    async function handleSubmit({newPassword}, {setSubmitting, setFieldError}) {
        try {
            const res = await change({
                variables: {
                    userId,
                    token,
                    password: newPassword,
                },
            });
            if (res.data.resetPasswordV2.error) {
                setFieldError("serverError", res.data.resetPasswordV2.error);
                return;
            }

            if (res.data.resetPasswordV2.redirectUrl) {
                push(res.data.resetPasswordV2.redirectUrl);
            } else {
                push("/auth/login");
            }
        } catch (error) {
            console.log(error);
            setFieldError("serverError", "An error occurred. Please try again later.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <View padding="medium">
            <Form
                onSubmit={handleSubmit}
                initialValues={{newPassword: "", confirmNewPassword: "", serverError: ""}}
                validationSchema={{
                    newPassword: string().label("New Password").required().min(PASSWORD_MIN_LENGTH),
                    confirmNewPassword: string()
                        .label("Confirm New Password")
                        .required()
                        .min(PASSWORD_MIN_LENGTH)
                        .oneOf([ref("newPassword"), null], "Passwords don't match"),
                }}
            >
                {user ? (
                    <input aria-label="email" disabled hidden name="email" value={user.email} />
                ) : null}
                <Stack space="large">
                    <ErrorMessage
                        name="serverError"
                        component={
                            <MutationErrorHandler
                                error={error}
                                meta={{
                                    component: "ChangePasswordForm",
                                    mutation: "useChangeCommunityPasswordMutation",
                                }}
                            />
                        }
                    />

                    <FormGroup label="New Password">
                        <TextInput type="password" name="newPassword" autoComplete="new-password" />
                    </FormGroup>
                    <FormGroup label="Confirm New Password">
                        <TextInput
                            type="password"
                            name="confirmNewPassword"
                            autoComplete="new-password"
                        />
                    </FormGroup>
                    <SubmitButton block>Change Password</SubmitButton>
                </Stack>
            </Form>
        </View>
    );
}
